/**
 * CustomDashboard CRUD + PromQL range-query hooks.
 *
 * Wraps the controller's two restored endpoints
 * (``/api/v1/dashboards``  and ``/api/v1/promql/query_range``) and
 * keeps their request/response shapes in one place so the Insights
 * tab can stay component-only.
 *
 * The PromQL hook is a `useQuery` keyed on
 * ``(scope, panel-id, range-bucket, expressions)`` — the range is
 * truncated to a 5s bucket so the auto-refresh loop produces a stable
 * key for the duration of a tick instead of re-querying every render.
 */
import { useCallback, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";

// ---- shared scope shape -------------------------------------------------

export type DashboardScopeKind = "global" | "service" | "pool";

export interface DashboardScope {
  kind: DashboardScopeKind;
  service_id?: number | null;
  pool_id?: number | null;
}

// ---- panel / dashboard shapes ------------------------------------------

export const PANEL_UNITS = [
  "reqps",
  "ms",
  "short",
  "usd",
  "percent",
  "bytes",
] as const;
export type PanelUnit = (typeof PANEL_UNITS)[number];

export const PANEL_KINDS = ["timeseries", "stat"] as const;
export type PanelKind = (typeof PANEL_KINDS)[number];

export interface PanelSeries {
  legend: string;
  promql: string;
  color?: string | null;
}

export interface Panel {
  id: string;
  title: string;
  kind: PanelKind;
  unit: PanelUnit;
  w: number;
  h: number;
  stacked: boolean;
  series: PanelSeries[];
}

export interface CustomDashboard {
  id: number;
  name: string;
  scope: DashboardScope;
  panels: Panel[];
}

export interface DashboardCreate {
  name: string;
  scope: DashboardScope;
  panels: Panel[];
}

export interface DashboardPatch {
  name?: string;
  panels?: Panel[];
}

// ---- list / CRUD --------------------------------------------------------

const LIST_KEY = (scope: DashboardScope) =>
  ["dashboards", scope.kind, scope.service_id ?? null, scope.pool_id ?? null] as const;

function listQuery(scope: DashboardScope): string {
  const qs = new URLSearchParams({ scope: scope.kind });
  if (scope.service_id != null) qs.set("service_id", String(scope.service_id));
  if (scope.pool_id != null) qs.set("pool_id", String(scope.pool_id));
  return `/dashboards?${qs.toString()}`;
}

export function useDashboards(scope: DashboardScope) {
  return useQuery({
    queryKey: LIST_KEY(scope),
    queryFn: () => api.get<CustomDashboard[]>(listQuery(scope)),
    staleTime: 10_000,
  });
}

export function useCreateDashboard(scope: DashboardScope) {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: (body: DashboardCreate) =>
      api.post<CustomDashboard>("/dashboards", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY(scope) }),
  });
  return useCallback(
    (body: DashboardCreate) => mutation.mutateAsync(body),
    [mutation],
  );
}

export function useUpdateDashboard(scope: DashboardScope) {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ id, body }: { id: number; body: DashboardPatch }) =>
      api.patch<CustomDashboard>(`/dashboards/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY(scope) }),
  });
  return useCallback(
    (id: number, body: DashboardPatch) => mutation.mutateAsync({ id, body }),
    [mutation],
  );
}

export function useDeleteDashboard(scope: DashboardScope) {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: (id: number) => api.delete<void>(`/dashboards/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY(scope) }),
  });
  return useCallback((id: number) => mutation.mutateAsync(id), [mutation]);
}

// ---- PromQL range query -------------------------------------------------

export interface PromQLQuery {
  id: string;
  promql: string;
}

export interface PromQLRangeRequest {
  range: { start: number; end: number; step_seconds: number };
  queries: PromQLQuery[];
  scope?: DashboardScope;
}

export interface PromQLPoint {
  ts_ms: number;
  value: number | null;
}

export interface PromQLSeries {
  id: string;
  labels: Record<string, string>;
  points: PromQLPoint[];
}

export interface PromQLRangeResponse {
  series: PromQLSeries[];
}

/**
 * Range options surfaced in the panel header. ``windowSeconds`` is the
 * lookback; ``stepSeconds`` is chosen so each window stays well under
 * the controller's 11k-sample cap.
 */
export const RANGE_OPTIONS = [
  { id: "15m", label: "15m", windowSeconds: 15 * 60, stepSeconds: 15 },
  { id: "1h", label: "1h", windowSeconds: 60 * 60, stepSeconds: 30 },
  { id: "6h", label: "6h", windowSeconds: 6 * 60 * 60, stepSeconds: 60 },
  { id: "24h", label: "24h", windowSeconds: 24 * 60 * 60, stepSeconds: 120 },
  { id: "7d", label: "7d", windowSeconds: 7 * 24 * 60 * 60, stepSeconds: 900 },
] as const;
export type RangeOptionId = (typeof RANGE_OPTIONS)[number]["id"];

/**
 * Run a panel's series in a single batched ``query_range`` request.
 *
 * The end timestamp is bucketed to ``refreshSeconds`` so React Query
 * sees a stable key for the lifetime of a tick — without this the
 * query key would change on every render and ``staleTime`` would
 * never kick in.
 */
export function usePanelRange(args: {
  panelId: string;
  series: PanelSeries[];
  scope: DashboardScope;
  windowSeconds: number;
  stepSeconds: number;
  refreshSeconds?: number;
  enabled?: boolean;
}) {
  const {
    panelId,
    series,
    scope,
    windowSeconds,
    stepSeconds,
    refreshSeconds = 30,
    enabled = true,
  } = args;

  const queries: PromQLQuery[] = useMemo(
    () =>
      series
        .filter((s) => s.promql.trim().length > 0)
        .map((s, idx) => ({ id: `${idx}`, promql: s.promql })),
    [series],
  );

  const bucketedEnd = Math.floor(Date.now() / 1000 / refreshSeconds) * refreshSeconds;
  const start = bucketedEnd - windowSeconds;

  return useQuery({
    queryKey: [
      "promql-range",
      panelId,
      scope.kind,
      scope.service_id ?? null,
      scope.pool_id ?? null,
      windowSeconds,
      stepSeconds,
      bucketedEnd,
      queries.map((q) => q.promql).join("||"),
    ],
    enabled: enabled && queries.length > 0,
    refetchInterval: refreshSeconds * 1000,
    staleTime: refreshSeconds * 1000,
    queryFn: () =>
      api.post<PromQLRangeResponse>("/promql/query_range", {
        range: { start, end: bucketedEnd, step_seconds: stepSeconds },
        queries,
        scope,
      } satisfies PromQLRangeRequest),
  });
}

// ---- starter dashboard --------------------------------------------------

/**
 * Seed panels for a brand-new service-scoped dashboard. Picked to be
 * useful for any HTTP-shaped workload behind Envoy: traffic, latency,
 * error rate, replica count. Uses the same ``$service`` placeholder
 * the controller substitutes server-side.
 */
export function defaultServicePanels(): Panel[] {
  return [
    {
      id: "rps",
      title: "Requests / sec",
      kind: "timeseries",
      unit: "reqps",
      w: 6,
      h: 240,
      stacked: false,
      series: [
        {
          legend: "rps",
          promql:
            'sum(rate(envoy_cluster_upstream_rq_total{envoy_cluster_name=~"$service.*"}[1m]))',
        },
      ],
    },
    {
      id: "latency",
      title: "Upstream latency (p50 / p95)",
      kind: "timeseries",
      unit: "ms",
      w: 6,
      h: 240,
      stacked: false,
      series: [
        {
          legend: "p50",
          promql:
            'histogram_quantile(0.5, sum by (le) (rate(envoy_cluster_upstream_rq_time_bucket{envoy_cluster_name=~"$service.*"}[5m])))',
        },
        {
          legend: "p95",
          promql:
            'histogram_quantile(0.95, sum by (le) (rate(envoy_cluster_upstream_rq_time_bucket{envoy_cluster_name=~"$service.*"}[5m])))',
        },
      ],
    },
    {
      id: "errors",
      title: "5xx error rate",
      kind: "timeseries",
      unit: "percent",
      w: 6,
      h: 240,
      stacked: false,
      series: [
        {
          legend: "5xx %",
          promql:
            '100 * sum(rate(envoy_cluster_upstream_rq_xx{envoy_response_code_class="5",envoy_cluster_name=~"$service.*"}[5m])) / clamp_min(sum(rate(envoy_cluster_upstream_rq_total{envoy_cluster_name=~"$service.*"}[5m])), 0.001)',
        },
      ],
    },
    {
      id: "replicas",
      title: "Ready replicas",
      kind: "timeseries",
      unit: "short",
      w: 6,
      h: 240,
      stacked: false,
      series: [
        {
          legend: "ready",
          promql:
            'sum(backstage_replica_status{service=~"$service",status="ready"})',
        },
      ],
    },
  ];
}
